<template>
  <div class="wallet-container">
    <v-expansion-panel popout light class="futuristic-expansion">
      <v-expansion-panel-content>
        <template v-slot:header>
          <div>
            <h4 class="earth-color">WALLET OVERVIEW</h4>
            <div class="title-decoration"></div>
          </div>
        </template>
        <v-card flat class="futuristic-card">
          <v-container fluid>
            <v-layout row wrap>
              <v-flex lg6 md6 sm12 xs12>
                <v-autocomplete
                  label="Filter by Currency"
                  v-model="selectedCurrency"
                  :items="currencyOptions"
                  item-text="name"
                  item-value="public_id"
                  class="futuristic-input"
                ></v-autocomplete>
              </v-flex>
              <v-flex lg6 md6 sm12 xs12>
                <v-btn depressed @click="refreshBalance" color="#587e27" :loading="loading" class="futuristic-btn">
                  <v-icon left>refresh</v-icon>
                  REFRESH
                </v-btn>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>

    <!-- Wallet Balance Card - Same style as payment page -->
    <v-layout row wrap class="mb-4">
      <v-flex lg12 md12 sm12 xs12>
        <v-card class="futuristic-card balance-card" hover>
          <v-card-text>
            <div class="wallet-balance-wrapper">
              <div class="wallet-header-section">
                <div class="balance-icon">
                  <v-icon large color="#587e27">account_balance_wallet</v-icon>
                </div>
                <div class="balance-title">
                  <span class="balance-label">WALLET BALANCE</span>
                </div>
              </div>
              
              <!-- Dual Currency Display - Same as payment page -->
              <div class="wallet-balances">
                <div class="wallet-balance-item">
                  <span class="balance-label">KES Balance:</span>
                  <span class="balance-amount" :class="{ 'text-success': localBalanceKes > 0, 'text-danger': localBalanceKes === 0 }">
                    {{ formatPrice(localBalanceKes) }} KES
                  </span>
                </div>
                <div class="wallet-balance-item">
                  <span class="balance-label">USD Balance:</span>
                  <span class="balance-amount" :class="{ 'text-success': localBalanceUsd > 0, 'text-danger': localBalanceUsd === 0 }">
                    {{ formatPrice(localBalanceUsd) }} USD
                  </span>
                </div>
              </div>
              
              <div class="balance-footer">
                <!--<v-btn small depressed color="#587e27" class="futuristic-btn" @click="showDepositDialog">
                  <v-icon small left>add</v-icon>
                  Deposit
                </v-btn>-->
                <v-btn small depressed outlined color="#587e27" class="futuristic-btn ml-2" @click="refreshBalance" :loading="loading">
                  <v-icon small left>refresh</v-icon>
                  Refresh
                </v-btn>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-card flat class="futuristic-card">
      <v-card-title class="card-header">
        <div>
          <h4 class="earth-color">WALLET TRANSACTIONS ({{ filteredTransactions.length }})</h4>
          <div class="title-decoration"></div>
        </div>
        <v-spacer></v-spacer>
        <v-text-field append-icon="search" label="Search" single-line hide-details v-model="search" class="futuristic-search"></v-text-field>
      </v-card-title>
      
      <v-data-table
        :headers="columns"
        :items="filteredTransactions"
        :search="search"
        :rows-per-page-items="[10, 25, 50, { text: 'All', value: -1 }]"
        :loading="transactionsLoading"
        class="futuristic-table"
      >
        <v-progress-linear slot="progress" color="#587e27" indeterminate></v-progress-linear>
        
        <template v-slot:items="props">
          <td data-label="Date">{{ formatDateTime(props.item.created_at) }}</td>
          <td data-label="Reference">{{ props.item.reference }}</td>
          <td data-label="Type">
            <span :class="getTransactionTypeClass(props.item.transaction_type)">
              {{ (props.item.transaction_type || 'N/A').toUpperCase() }}
            </span>
          </td>
          <td data-label="Amount" :class="getAmountClass(props.item.amount)">
            {{ getCurrencySignFromPublicId(props.item.currency_public_id) }} {{ formatPrice(Math.abs(props.item.amount)) }}
          </td>
          <td data-label="Status">
            <span :style="getPaymentStatusStyle(props.item.payment_status)">
              {{ (props.item.payment_status || 'N/A').toUpperCase() }}
            </span>
          </td>
          <td data-label="Payment Method">{{ props.item.payment_method || 'N/A' }}</td>
          
          <td data-label="Booking Ref">
            <router-link 
              v-if="props.item.booking_ref_code"
              :to="{ name: 'Booking', params: { booking_id: props.item.booking_id || props.item.booking_ref_code }}"
            >
              <u class="futuristic-link">{{ props.item.booking_ref_code }}</u>
            </router-link>
            <span v-else class="grey--text">-</span>
          </td>
          
          <td data-label="Deposit Ref">
            <span v-if="props.item.deposit_reference" class="deposit-ref">
              {{ props.item.deposit_reference }}
            </span>
            <span v-else class="grey--text">-</span>
          </td>
          
          <td data-label="Description">{{ props.item.description || 'N/A' }}</td>
        </template>
        
        <v-alert slot="no-results" :value="true" color="error" icon="warning" class="futuristic-alert">
          Your search for "{{ search }}" found no results.
        </v-alert>
        <v-alert slot="no-data" :value="true" color="info" icon="info" class="futuristic-alert">
          No transactions found.
        </v-alert>
      </v-data-table>
      
      <!-- GET MORE and GET ALL buttons -->
      <v-layout row wrap class="mt-3" v-if="localTransactions.length > 0">
        <v-flex xs12 sm6>
          <v-btn
            v-if="hasMore"
            :depressed="true"
            @click="getMore()"
            color="#587e27"
            :loading="loadingMore"
            class="futuristic-btn"
          >GET MORE</v-btn>
          <v-btn
            v-if="hasMore"
            :depressed="true"
            @click="getAllTransactions()"
            color="#e84118"
            :loading="loadingAll"
            class="futuristic-btn ml-2"
          >GET ALL</v-btn>
        </v-flex>
      </v-layout>
    </v-card>

    <!-- Deposit Dialog -->
    <v-dialog v-model="depositDialog" max-width="500px">
      <v-card>
        <v-card-title class="card-header">
          <span class="earth-color">Deposit to Wallet</span>
        </v-card-title>
        <v-card-text>
          <v-text-field label="Amount" v-model="depositAmount" type="number" class="futuristic-input" required></v-text-field>
          
          <v-select 
            label="Deposit Currency" 
            v-model="depositCurrencyPublicId" 
            :items="depositCurrencies"
            item-text="name"
            item-value="public_id"
            class="futuristic-input"
          ></v-select>
          
          <v-select label="Payment Method" v-model="paymentMethod" :items="availablePaymentMethods" class="futuristic-input">
            <template slot="item" slot-scope="data">
              <v-icon left small>{{ getPaymentMethodIcon(data.item) }}</v-icon>
              {{ getPaymentMethodLabel(data.item) }}
            </template>
          </v-select>
          
          <div v-if="paymentMethod === 'mpesa'">
            <v-text-field label="M-Pesa Phone Number" v-model="mpesaPhoneNumber" type="tel" prefix="" placeholder="712345678" class="futuristic-input" required></v-text-field>
            <v-alert :value="true" type="info" class="mb-2">
              <div class="caption">You'll receive an M-Pesa prompt on your phone to complete payment</div>
            </v-alert>
            
            <div class="payment-actions">
              <v-btn depressed color="primary" block large @click="processMpesaPayment" :loading="processingPayment">
                <v-icon left>mdi-send</v-icon>
                PAY WITH M-PESA
              </v-btn>
            </div>
          </div>

          <div v-if="paymentMethod === 'card'">
            <form id="iveriWalletForm" method="POST" action="https://portal.host.iveri.com/Lite/Authorise.aspx" target="_blank">
              <input name="Lite_Merchant_Applicationid" type="hidden" :value="getMerchantId()" />
              <input name="Lite_Order_Amount" type="hidden" :value="getIveriAmount()" />
              <input name="Lite_Currency_AlphaCode" type="hidden" :value="getDepositCurrencySign()" />
              <input name="Lite_Website_Successful_url" type="hidden" :value="iveriCatcherUrl + 'wallet-deposit-success'" />
              <input name="Lite_Website_Fail_url" type="hidden" :value="iveriCatcherUrl + 'wallet-deposit-failure'" />
              <input name="Lite_Website_TryLater_url" type="hidden" :value="iveriCatcherUrl + 'wallet-deposit-failure'" />
              <input name="Lite_Website_Error_url" type="hidden" :value="iveriCatcherUrl + 'wallet-deposit-failure'" />
              <input name="Lite_Order_LineItems_Product_1" type="hidden" value="Wallet Deposit" />
              <input name="Lite_Order_LineItems_Quantity_1" type="hidden" value="1" />
              <input name="Lite_Order_LineItems_Amount_1" type="hidden" :value="getIveriAmount()" />
              <input name="Lite_ConsumerOrderID_PreFix" type="hidden" value="WLT" />
              <input name="Ecom_BillTo_Online_Email" type="hidden" :value="depositEmail" />
              <input name="Ecom_Payment_Card_Protocols" type="hidden" value="iVeri" />
              <input name="Ecom_TransactionComplete" type="hidden" value="" />
              <input name="CURRENCY_PUBLIC_ID" type="hidden" :value="depositCurrencyPublicId" />
              <input name="USER_PUBLIC_ID" type="hidden" :value="userPublicId" />
              <input name="DEPOSIT_AMOUNT" type="hidden" :value="depositAmount" />
              <input name="WALLET_REFERENCE" type="hidden" :value="generateWalletReference()" />

              <v-text-field label="Email Address"
                            v-model="depositEmail"
                            :rules="emailRules"
                            required
                            prepend-icon="mdi-email"
                            class="futuristic-input"></v-text-field>

              <div class="payment-actions">
                <v-btn depressed type="submit" color="primary" large block>
                  <v-icon left>mdi-lock</v-icon>
                  PROCEED TO SECURE PAYMENT
                </v-btn>
              </div>

              <div class="payment-security">
                <v-icon color="green">mdi-shield-check</v-icon>
                <span>Your payment is secured with 256-bit encryption</span>
              </div>

              <div class="email-confirmation earth-color">
                <v-icon color="earth">mdi-alert-circle</v-icon>
                PLEASE RE-CONFIRM YOUR EMAIL ADDRESS TO PROCEED WITH PAYMENT
              </div>
            </form>
          </div>
          
          <v-textarea label="Description (Optional)" v-model="depositDescription" class="futuristic-input"></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="#e84118" flat @click="closeDepositDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="mpesaDialog" max-width="400px" persistent>
      <v-card>
        <v-card-title class="card-header">
          <span class="earth-color">M-Pesa Payment Initiated</span>
        </v-card-title>
        <v-card-text class="text-xs-center">
          <v-icon size="64" color="#587e27" class="mb-3">phone_android</v-icon>
          <p>Check your phone {{ formatPhoneNumber(mpesaPhoneNumber) }}</p>
          <v-progress-circular indeterminate color="#587e27" class="mt-3"></v-progress-circular>
          <p class="mt-3 caption">Waiting for payment confirmation...</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error" flat @click="cancelMpesaPayment">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: 'WalletBalance',
  
  data: function() {
    return {
      search: '',
      loading: false,
      transactionsLoading: false,
      processingPayment: false,
      loadingMore: false,
      loadingAll: false,
      currentPage: 2,
      hasMore: true,
      selectedCurrency: 'all',
      currencies: [
        { public_id: 'all', name: 'All Transactions', sign: '' },
        { public_id: '162fface-f5f1-41de-913b-d2bb784dda3a', name: 'KES', sign: 'KES' },
        { public_id: '6bf74e49-7ff6-4555-a080-15541d62204a', name: 'USD', sign: 'USD' }
      ],
      
      depositDialog: false,
      depositAmount: '',
      depositCurrencyPublicId: '162fface-f5f1-41de-913b-d2bb784dda3a',
      depositCurrencies: [
        { public_id: '162fface-f5f1-41de-913b-d2bb784dda3a', name: 'KES', sign: 'KES' },
        { public_id: '6bf74e49-7ff6-4555-a080-15541d62204a', name: 'USD', sign: 'USD' }
      ],
      paymentMethod: 'mpesa',
      availablePaymentMethods: ['mpesa', 'card'],
      depositDescription: '',
      depositEmail: '',
      emailRules: [
        function(v) { return !!v || 'Email is required' },
        function(v) { return /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(v) || 'Must be a valid email' }
      ],
      
      mpesaPhoneNumber: '',
      mpesaDialog: false,
      mpesaCheckInterval: null,
      currentMpesaReference: null,
      
      iveriCatcherUrl: 'https://bookings.olpejetaconservancy.org:5019/',
      
      localTransactions: [],
      localBalanceUsd: 0,
      localBalanceKes: 0,
      
      columns: [
        { text: 'Date', value: 'created_at' },
        { text: 'Reference', value: 'reference' },
        { text: 'Type', value: 'transaction_type' },
        { text: 'Amount', value: 'amount' },
        { text: 'Status', value: 'payment_status' },
        { text: 'Payment Method', value: 'payment_method' },
        { text: 'Booking Ref', value: 'booking_ref_code' },
        { text: 'Deposit Ref', value: 'deposit_reference' }, 
        { text: 'Description', value: 'description' }
      ]
    }
  },
  
  computed: {
    currencyOptions: function() {
      return this.currencies
    },

    walletReference: function() {
      if (this.depositDialog && this.paymentMethod === 'card') {
        return 'WLT' + Date.now() + '-' + Math.random().toString(36).substr(2, 8).toUpperCase();
      }
      return '';
    },
    
    displayCurrencyPublicId: function() {
      return this.$store.state.display_currency_public_id || '162fface-f5f1-41de-913b-d2bb784dda3a'
    },
    
    displayCurrencySign: function() {
      return this.displayCurrencyPublicId === '162fface-f5f1-41de-913b-d2bb784dda3a' ? 'KES' : 'USD'
    },
    
    displayedBalance: function() {
      if (this.displayCurrencyPublicId === '162fface-f5f1-41de-913b-d2bb784dda3a') {
        return this.localBalanceKes
      } else {
        return this.localBalanceUsd
      }
    },
    
    live_id: function() {
      return this.$store.state.live_id
    },
    
    kes_live_id: function() {
      return this.$store.state.kes_live_id
    },
    
    userPublicId: function() {
      var id = localStorage.getItem('user_public_id')
      if (id) return id
      var userData = localStorage.getItem('user_data')
      if (userData) {
        try {
          var parsed = JSON.parse(userData)
          if (parsed.public_id) return parsed.public_id
        } catch(e) {}
      }
      return null
    },
    
    userEmail: function() {
      var email = localStorage.getItem('user_email')
      if (email) return email
      var userData = localStorage.getItem('user_data')
      if (userData) {
        try {
          var parsed = JSON.parse(userData)
          if (parsed.email) return parsed.email
        } catch(e) {}
      }
      return null
    },
    
    userPhone: function() {
      var phone = localStorage.getItem('user_phone')
      if (phone) return phone
      var userData = localStorage.getItem('user_data')
      if (userData) {
        try {
          var parsed = JSON.parse(userData)
          if (parsed.phone_number) return parsed.phone_number
        } catch(e) {}
      }
      return null
    },
    
    allTransactions: function() {
      return this.localTransactions
    },
    
    filteredTransactions: function() {
      var self = this
      var filtered = self.allTransactions.slice()
      if (self.selectedCurrency !== 'all') {
        filtered = filtered.filter(function(tx) {
          return tx.currency_public_id === self.selectedCurrency
        })
      }
      return filtered
    }
  },
  
  created: function() {
    this.loadWalletData()
    if (this.userEmail) {
      this.depositEmail = this.userEmail
    }
    
    this.$root.$on('currency-changed', this.handleCurrencyChange)
  },
  
  beforeDestroy: function() {
    if (this.mpesaCheckInterval) {
      clearInterval(this.mpesaCheckInterval)
    }
    this.$root.$off('currency-changed', this.handleCurrencyChange)
  },
  
  methods: {
    handleCurrencyChange: function(currencyData) {
      this.loadWalletData()
    },

    generateWalletReference: function() {
      return 'WLT' + Date.now() + '-' + Math.random().toString(36).substr(2, 8).toUpperCase();
    },
    
    getCurrencySignFromPublicId: function(publicId) {
      if (publicId === '162fface-f5f1-41de-913b-d2bb784dda3a') return 'KES'
      if (publicId === '6bf74e49-7ff6-4555-a080-15541d62204a') return 'USD'
      return 'KES'
    },
    
    getDepositCurrencySign: function() {
      return this.depositCurrencyPublicId === '162fface-f5f1-41de-913b-d2bb784dda3a' ? 'KES' : 'USD'
    },
    
    getMerchantId: function() {
      if (this.getDepositCurrencySign() === 'KES') {
        return this.kes_live_id
      } else {
        return this.live_id
      }
    },
    
    loadWalletData: function() {
      var self = this
      var userId = self.userPublicId
      
      if (!userId) {
        console.warn('No user_public_id found')
        return
      }
      
      self.transactionsLoading = true
      self.currentPage = 2
      self.hasMore = true
      
      self.$store.dispatch('get_wallet_balance', userId)
        .then(function(res) {
          if (res && res.status === 'success') {
            self.localBalanceUsd = res.data.wallet_balance_usd || 0
            self.localBalanceKes = res.data.wallet_balance_kes || 0
          }
        })
        .catch(function(err) {
          console.error('Error loading balance:', err)
        })
      
      self.$store.dispatch('get_wallet_transactions', userId)
        .then(function(res) {
          if (res && res.status === 'success') {
            self.localTransactions = res.data || []
            self.hasMore = res.data && res.data.length >= 15
          }
          self.transactionsLoading = false
        })
        .catch(function(err) {
          console.error('Error loading transactions:', err)
          self.transactionsLoading = false
        })
    },
    
    getMore: function() {
      var self = this
      var userId = self.userPublicId
      
      self.loadingMore = true
      
      self.$store.dispatch('get_more_wallet_transactions', {
        userPublicId: userId,
        page: self.currentPage
      })
        .then(function(res) {
          if (res && res.status === 'success') {
            if (res.data && res.data.length > 0) {
              self.localTransactions = [...self.localTransactions, ...res.data]
              self.currentPage++
              self.hasMore = res.data.length >= 15
            } else {
              self.hasMore = false
            }
          }
          self.loadingMore = false
        })
        .catch(function(err) {
          console.error('Error loading more transactions:', err)
          self.loadingMore = false
        })
    },
    
    getAllTransactions: function() {
      var self = this
      var userId = self.userPublicId
      
      self.loadingAll = true
      
      self.$store.dispatch('get_all_wallet_transactions', userId)
        .then(function(res) {
          if (res && res.status === 'success') {
            self.localTransactions = res.data || []
            self.hasMore = false
          }
          self.loadingAll = false
        })
        .catch(function(err) {
          console.error('Error loading all transactions:', err)
          self.loadingAll = false
        })
    },
    
    refreshBalance: function() {
      this.localTransactions = []
      this.currentPage = 2
      this.hasMore = true
      this.loadWalletData()
    },
    
    showDepositDialog: function() {
      this.depositAmount = ''
      this.depositDescription = ''
      this.paymentMethod = 'mpesa'
      this.depositEmail = this.userEmail || ''
      this.depositCurrencyPublicId = '162fface-f5f1-41de-913b-d2bb784dda3a'
      
      if (this.userPhone) {
        this.mpesaPhoneNumber = this.userPhone
      } else {
        this.mpesaPhoneNumber = ''
      }
      
      this.depositDialog = true
    },
    
    closeDepositDialog: function() {
      this.depositDialog = false
    },
    
    getIveriAmount: function() {
      var amount = parseFloat(this.depositAmount)
      if (isNaN(amount)) amount = 0
      return Math.round(amount * 100)
    },
    
    processMpesaPayment: function() {
      var self = this
      var phone = self.formatPhoneNumber(self.mpesaPhoneNumber)
      
      if (!phone) {
        alert('Please enter a valid M-Pesa phone number')
        return
      }
      
      var userPublicId = self.userPublicId
      
      if (!userPublicId) {
        alert('User not logged in. Please refresh and try again.')
        return
      }
      
      self.processingPayment = true
      var amount = parseFloat(self.depositAmount)
      var reference = 'DEP-MPESA-' + Date.now() + '-' + Math.random().toString(36).substr(2, 6)
      self.currentMpesaReference = reference
      
      var requestData = {
        user_public_id: userPublicId,
        amount: amount,
        currency_public_id: self.depositCurrencyPublicId,
        phone: phone,
        reference: reference,
        description: self.depositDescription || 'Wallet deposit via M-Pesa'
      }
      
      self.$store.dispatch('process_mpesa_payment', requestData)
        .then(function(res) {
          self.processingPayment = false
          if (res && res.status === 'success') {
            self.depositDialog = false
            self.mpesaDialog = true
            self.startMpesaCheck(reference)
          } else {
            var errorMsg = (res && res.message) || 'M-Pesa payment failed'
            alert(errorMsg)
          }
        })
        .catch(function(err) {
          console.error('M-Pesa payment error:', err)
          self.processingPayment = false
          var errorMsg = 'Failed to initiate M-Pesa payment'
          if (err.response && err.response.data && err.response.data.message) {
            errorMsg = err.response.data.message
          }
          alert(errorMsg)
        })
    },
    
    startMpesaCheck: function(reference) {
      var self = this
      var attempts = 0
      var maxAttempts = 30
      
      self.mpesaCheckInterval = setInterval(function() {
        attempts++
        
        self.$store.dispatch('get_wallet_transactions', self.userPublicId)
          .then(function(res) {
            if (res && res.status === 'success') {
              var transactions = res.data || []
              var matchingTx = null
              
              for (var i = 0; i < transactions.length; i++) {
                if (transactions[i].reference === reference) {
                  matchingTx = transactions[i]
                  break
                }
              }
              
              if (matchingTx) {
                if (matchingTx.payment_status === 'completed') {
                  clearInterval(self.mpesaCheckInterval)
                  self.mpesaDialog = false
                  alert('Payment successful! Wallet updated.')
                  self.loadWalletData()
                } else if (matchingTx.payment_status === 'failed') {
                  clearInterval(self.mpesaCheckInterval)
                  self.mpesaDialog = false
                  alert('Payment failed. Please try again.')
                }
              }
            }
          })
          .catch(function(err) {
            console.error('Error checking payment status:', err)
          })
        
        if (attempts >= maxAttempts) {
          clearInterval(self.mpesaCheckInterval)
          self.mpesaDialog = false
          alert('Payment confirmation timeout. Please refresh to see updated balance.')
          self.loadWalletData()
        }
      }, 4000)
    },
    
    cancelMpesaPayment: function() {
      if (this.mpesaCheckInterval) {
        clearInterval(this.mpesaCheckInterval)
        this.mpesaCheckInterval = null
      }
      this.mpesaDialog = false
    },
    
    formatPrice: function(price) {
      if (!price && price !== 0) return '0.00'
      return parseFloat(price).toLocaleString('en-KE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    },
    
    formatDateTime: function(date) {
      if (!date) return 'N/A'
      if (this.$moment) {
        var parsedDate = this.$moment(date)
        if (parsedDate.isValid()) {
          return parsedDate.format('MMM DD, YYYY HH:mm')
        }
      }
      return new Date(date).toLocaleString()
    },
    
    formatPhoneNumber: function(phone) {
      if (!phone) return null
      var cleaned = phone.toString().replace(/\D/g, '')
      if (cleaned.startsWith('0')) return '254' + cleaned.substring(1)
      if (cleaned.startsWith('254')) return cleaned
      if (cleaned.length === 9) return '254' + cleaned
      return cleaned
    },
    
    getTransactionTypeClass: function(type) {
      if (type === 'deposit' || type === 'refund') return 'transaction-positive'
      if (type === 'payment') return 'transaction-negative'
      return 'transaction-neutral'
    },
    
    getAmountClass: function(amount) {
      if (amount > 0) return 'amount-positive'
      if (amount < 0) return 'amount-negative'
      return 'amount-neutral'
    },
    
    getPaymentStatusStyle: function(status) {
      var colors = {
        'completed': '#28a745',
        'pending': '#ffc107',
        'failed': '#dc3545',
        'cancelled': '#6c757d'
      }
      var statusLower = status ? status.toLowerCase() : ''
      var color = colors[statusLower] || '#007bff'
      return 'background-color: ' + color + '; color: #fff; padding: 4px 8px; border-radius: 4px; display: inline-block; font-size: 12px; font-weight: 500;'
    },
    
    getPaymentMethodIcon: function(method) {
      var icons = {
        'mpesa': 'phone_android',
        'card': 'credit_card'
      }
      return icons[method] || 'payment'
    },
    
    getPaymentMethodLabel: function(method) {
      var labels = {
        'mpesa': 'M-Pesa',
        'card': 'Credit/Debit Card (iVeri)'
      }
      return labels[method] || method.toUpperCase()
    }
  }
}
</script>

<style scoped>
.wallet-container {
  background: #ffffff;
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;
}
.wallet-container:hover { box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1); }

/* Balance Card - Same style as payment page */
.balance-card { 
  margin: 8px; 
  position: relative; 
  overflow: hidden; 
  transition: all 0.3s ease;
  background: #f5f9f0;
  border: 1px solid #e0e8d0;
  border-radius: 10px;
}
.balance-card:hover { 
  transform: translateY(-5px); 
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15); 
}

.wallet-balance-wrapper {
  padding: 4px 0;
}

.wallet-header-section {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.balance-icon {
  opacity: 0.6;
}

.balance-title {
  flex: 1;
}

.balance-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
  letter-spacing: 1px;
}

/* Dual Currency Display - Exact same as payment page */
.wallet-balances {
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
  margin: 16px 0;
}

.wallet-balance-item {
  padding: 10px 15px;
  background: white;
  border-radius: 8px;
  flex: 1;
  min-width: 150px;
  border-left: 4px solid #587e27;
}

.wallet-balance-item:last-child {
  border-left-color: #1a73e8;
}

.wallet-balance-item .balance-label {
  font-weight: 500;
  color: #666;
  display: block;
  font-size: 0.85rem;
  margin-bottom: 4px;
}

.wallet-balance-item .balance-amount {
  font-size: 1.3rem;
  font-weight: bold;
  display: block;
}

.text-success {
  color: #28a745;
}

.text-danger {
  color: #dc3545;
}

.balance-footer {
  display: flex;
  justify-content: flex-start;
  margin-top: 12px;
  flex-wrap: wrap;
  gap: 8px;
}

/* Rest of your existing styles */
.card-header { 
  background: linear-gradient(45deg, #e8eaf6, #f5f7fa); 
  padding: 16px; 
  border-radius: 8px; 
}
.earth-color { 
  color: #587e27; 
  font-weight: 600; 
  margin: 0; 
  font-size: 1.5rem; 
}
.title-decoration { 
  height: 4px; 
  background: linear-gradient(90deg, #587e27, transparent); 
  margin-bottom: 16px; 
  width: 100px; 
}
.futuristic-card { 
  border-radius: 8px; 
  background: #ffffff; 
  transition: all 0.3s ease; 
  margin-top: 8px; 
}
.futuristic-card:hover { 
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1); 
}
.futuristic-input >>> .v-input__slot { 
  border-radius: 8px; 
  background: #f5f7fa; 
}
.futuristic-search { 
  max-width: 300px; 
}
.deposit-ref {
  font-family: monospace;
  font-size: 12px;
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
  letter-spacing: 0.5px;
}

.futuristic-link {
  color: #587e27;
  text-decoration: none;
  cursor: pointer;
}

.futuristic-link:hover {
  color: #1b5e20;
  text-decoration: underline;
}
.futuristic-search >>> .v-input__slot { 
  border-radius: 8px; 
  background: #f5f7fa; 
}
.futuristic-btn { 
  border-radius: 8px; 
  text-transform: none; 
  font-weight: 500; 
  transition: all 0.2s ease; 
}
.futuristic-btn:hover { 
  transform: translateY(-1px); 
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); 
}
.futuristic-table { 
  border-radius: 8px; 
  background: #ffffff; 
}
.futuristic-table >>> thead tr th { 
  background: linear-gradient(45deg, #e8eaf6, #f5f7fa); 
  color: #587e27; 
  font-weight: 600; 
  font-size: 13px; 
}
.futuristic-table >>> tbody tr:hover { 
  background-color: #f5f7fa; 
}
.transaction-positive { 
  color: #28a745; 
  font-weight: 600; 
}
.transaction-negative { 
  color: #dc3545; 
  font-weight: 600; 
}
.transaction-neutral { 
  color: #6c757d; 
}
.amount-positive { 
  color: #28a745; 
  font-weight: 700; 
}
.amount-negative { 
  color: #dc3545; 
  font-weight: 700; 
}
.futuristic-alert { 
  border-radius: 8px; 
  background: #fff5f5; 
}
.payment-security { 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  gap: 8px; 
  margin: 15px 0; 
}
.email-confirmation { 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  padding: 10px; 
  background-color: #fff8e1; 
  border-radius: 5px; 
  margin-top: 10px; 
}
.payment-actions { 
  margin: 25px 0; 
}

@media (max-width: 960px) {
  .wallet-container { padding: 12px; }
  .balance-amount { font-size: 24px; }
  .earth-color { font-size: 1.25rem; }
  .futuristic-btn { font-size: 0.9rem; }
  .futuristic-search { max-width: 200px; }
}

@media (max-width: 600px) {
  .wallet-container { padding: 8px; border-radius: 6px; }
  .balance-card { margin: 4px; }
  
  .wallet-balances {
    flex-direction: column;
    gap: 10px;
  }
  
  .wallet-balance-item {
    min-width: 100%;
  }
  
  .wallet-balance-item .balance-amount {
    font-size: 1.1rem;
  }
  
  .balance-footer { 
    flex-direction: column; 
    width: 100%;
  }
  .balance-footer .futuristic-btn { 
    margin: 4px 0 !important; 
    width: 100%;
  }
  .earth-color { font-size: 1.1rem; }
  .futuristic-btn { font-size: 0.85rem; }
  
  .futuristic-table >>> thead { display: none; }
  .futuristic-table >>> tbody tr td { 
    display: flex; 
    justify-content: space-between; 
    align-items: center; 
    padding: 8px 12px; 
    border-bottom: 1px solid #e0e0e0; 
  }
  .futuristic-table >>> tbody tr td:last-child { border-bottom: none; }
  .futuristic-table >>> tbody tr td::before { 
    content: attr(data-label); 
    font-weight: 600; 
    color: #587e27; 
    margin-right: 16px; 
    min-width: 100px; 
  }
  .futuristic-search { max-width: 100%; margin-top: 8px; }
  .card-header { flex-direction: column; align-items: stretch; }
}
</style>
